(() => {
  if (customElements.get('shipping-estimate')) return;
  const endpoint = '/shipping-estimate';
  const storageKey = 'fr-shipping-destination-v1';
  const regions = {
    US: 'AL:Alabama|AK:Alaska|AZ:Arizona|AR:Arkansas|CA:California|CO:Colorado|CT:Connecticut|DE:Delaware|DC:District of Columbia|FL:Florida|GA:Georgia|HI:Hawaii|ID:Idaho|IL:Illinois|IN:Indiana|IA:Iowa|KS:Kansas|KY:Kentucky|LA:Louisiana|ME:Maine|MD:Maryland|MA:Massachusetts|MI:Michigan|MN:Minnesota|MS:Mississippi|MO:Missouri|MT:Montana|NE:Nebraska|NV:Nevada|NH:New Hampshire|NJ:New Jersey|NM:New Mexico|NY:New York|NC:North Carolina|ND:North Dakota|OH:Ohio|OK:Oklahoma|OR:Oregon|PA:Pennsylvania|RI:Rhode Island|SC:South Carolina|SD:South Dakota|TN:Tennessee|TX:Texas|UT:Utah|VT:Vermont|VA:Virginia|WA:Washington|WV:West Virginia|WI:Wisconsin|WY:Wyoming|AA:Armed Forces Americas|AE:Armed Forces Europe|AP:Armed Forces Pacific|AS:American Samoa|GU:Guam|MP:Northern Mariana Islands|PR:Puerto Rico|VI:U.S. Virgin Islands',
    CA: 'AB:Alberta|BC:British Columbia|MB:Manitoba|NB:New Brunswick|NL:Newfoundland and Labrador|NS:Nova Scotia|NT:Northwest Territories|NU:Nunavut|ON:Ontario|PE:Prince Edward Island|QC:Quebec|SK:Saskatchewan|YT:Yukon',
  };
  Object.keys(regions).forEach((country) => { regions[country] = regions[country].split('|').map((s) => s.split(':')); });
  const readSaved = () => {
    try {
      const value = JSON.parse(sessionStorage.getItem(storageKey));
      if (/^[A-Z]{2}$/.test(value?.country) && Date.now() - value.at < 86400000) return value;
    } catch (_) { /* Storage is optional. */ }
  };
  let destination = readSaved();
  let cartPromise;
  const timeoutSignal = (ms) => {
    const controller = new AbortController();
    setTimeout(() => controller.abort(), ms);
    return controller.signal;
  };
  const currentCart = () => cartPromise ||= readJSON(`${window.Shopify?.routes?.root || '/'}cart.js`, { signal: timeoutSignal(6000) }).catch(() => null);
  async function readJSON(url, options = {}) {
    const response = await fetch(url, { credentials: 'same-origin', ...options });
    if (!response.ok) throw new Error('Shipping estimate unavailable');
    return response.json();
  }
  const geo = () => {
    if (typeof window !== 'undefined' && window.__frShippingGeoPromise) return window.__frShippingGeoPromise;
    const promise = readJSON(endpoint, { cache: 'no-store', signal: timeoutSignal(6000) }).catch(() => ({}));
    if (typeof window !== 'undefined') window.__frShippingGeoPromise = promise;
    return promise;
  };
  const countryLabel = (code) => {
    try { return new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'region' }).of(code); }
    catch (_) { return code; }
  };

  class ShippingEstimate extends HTMLElement {
    connectedCallback() {
      this.lifecycle = new AbortController();
      this.sequence = (this.sequence || 0) + 1;
      this.started = false;
      this.data = JSON.parse(this.querySelector('[data-shipping-data]').textContent);
      this.price = this.querySelector('[data-shipping-price]');
      this.progress = this.querySelector('[data-shipping-progress]');
      this.details = this.querySelector('[data-shipping-destination]');
      this.country = this.querySelector('[data-shipping-country]');
      this.region = this.querySelector('[data-shipping-region]');
      this.postal = this.querySelector('[data-shipping-postal]');
      this.regionText = this.querySelector('[data-shipping-region-text]');
      const options = { signal: this.lifecycle.signal };
      this.details.addEventListener('toggle', () => { if (this.details.open && !this.fieldsReady) this.prepareFields(); }, options);
      this.details.querySelector('summary').addEventListener('click', () => {
        if (!this.details.open) this.prepareFields();
      }, options);
      this.country.addEventListener('change', () => {
        this.fillRegions(this.country.value, '');
        if (this.postal) this.postal.value = '';
      }, options);
      this.querySelector('[data-shipping-update]').addEventListener('click', () => this.updateDestination(), options);
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.details.open) {
          this.details.open = false;
          this.details.querySelector('summary').focus();
          event.stopPropagation();
        } else if (event.key === 'Enter' && event.target === this.postal) {
          event.preventDefault();
          this.updateDestination();
        }
      }, options);
      window.addEventListener('fr:shipping-destination', () => {
        if (this.started && destination) { this.applyDestination(); this.refresh(); }
      }, options);
      if (this.dataset.context === 'product') {
        this.closest('product-info')?.addEventListener('change', (event) => {
          if (event.target.name === 'quantity' || event.target.name === 'id') this.scheduleRefresh();
        }, options);
      }
      if (typeof subscribe === 'function') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          cartPromise = undefined;
          if (this.dataset.context === 'product') { this.scheduleRefresh(); return; }
          this.updateCart(event.cartData);
        });
      }
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) { this.observer.disconnect(); this.start(); }
        }, { rootMargin: '200px' });
        this.observer.observe(this);
      } else this.start();
    }

    disconnectedCallback() {
      this.sequence++;
      this.lifecycle?.abort();
      this.request?.abort();
      clearTimeout(this.requestTimer);
      this.observer?.disconnect();
      this.unsubscribe?.();
      clearTimeout(this.timer);
    }

    async start() {
      if (this.started) return;
      this.started = true;
      const lifecycle = this.lifecycle;
      if (destination && destination.market !== this.dataset.country) {
        destination = undefined;
        try { sessionStorage.removeItem(storageKey); } catch (_) { /* Storage is optional. */ }
        window.dispatchEvent(new CustomEvent('fr:shipping-destination', { detail: { source: 'reset' } }));
      }
      if (!destination || destination.market !== this.dataset.country) {
        const geoResponse = await geo();
        const hint = geoResponse.destination || geoResponse;
        if (!this.isConnected || lifecycle.signal.aborted) return;
        if (destination?.at && destination.market === this.dataset.country) {
          this.details.hidden = false;
          this.applyDestination();
          this.refresh();
          return;
        }
        // A storefront country selection takes precedence over an IP hint.
        // Never reuse a state or postcode from another country/market.
        destination = {
          country: this.dataset.country, market: this.dataset.country,
          region: hint.country === this.dataset.country && regions[hint.country] ? (hint.region || hint.regionCode || '') : '',
          postal: '',
        };
      }
      this.details.hidden = false;
      this.applyDestination();
      this.refresh();
    }

    applyDestination() {
      this.destination = { ...destination };
      const { country, region } = this.destination;
      const regionName = regions[country]?.find(([code]) => code === region)?.[1];
      this.querySelector('[data-shipping-to]').textContent = `To ${regionName ? `${regionName}, ` : ''}${countryLabel(country)}`;
      if (this.fieldsReady) this.prepareFields();
    }

    prepareFields() {
      if (!this.fieldsReady) {
        let countries = [];
        try { countries = JSON.parse(document.getElementById('CountryLocalizationData')?.textContent).countries; } catch (_) { /* Single-country store. */ }
        const codes = new Set(countries.map(([code]) => code));
        codes.add(this.dataset.country);
        codes.add(this.destination.country);
        this.country.replaceChildren(...[...codes].map((code) => new Option(countryLabel(code), code)));
        this.fieldsReady = true;
      }
      this.country.value = this.destination.country;
      this.fillRegions(this.country.value, this.destination.region);
      if (this.postal) this.postal.value = this.destination.postal || '';
    }

    fillRegions(country, selected) {
      const entries = regions[country] || [];
      this.querySelector('[data-shipping-region-field]').hidden = entries.length === 0 && this.dataset.context !== 'cart';
      this.region.hidden = entries.length === 0;
      this.regionText.hidden = entries.length > 0;
      this.regionText.value = entries.length ? '' : (selected || '');
      this.region.setCustomValidity('');
      this.region.replaceChildren(new Option('Select state / province', ''), ...entries.map(([code, name]) => new Option(name, code)));
      this.region.value = selected || '';
    }

    updateDestination() {
      if (regions[this.country.value] && !this.region.value) {
        this.region.setCustomValidity('Select your state or province.');
        this.region.reportValidity();
        this.region.addEventListener('change', () => this.region.setCustomValidity(''), { once: true });
        return;
      }
      destination = {
        country: this.country.value, region: this.region.hidden ? this.regionText.value.trim() : this.region.value, postal: this.postal?.value.trim() || '',
        market: this.dataset.country, at: Date.now(), source: 'entered',
      };
      try { sessionStorage.setItem(storageKey, JSON.stringify(destination)); } catch (_) { /* Continue without storage. */ }
      this.details.open = false;
      this.details.querySelector('summary').focus();
      window.dispatchEvent(new CustomEvent('fr:shipping-destination', {
        detail: { country: destination.country, region: destination.region, market: destination.market, at: destination.at, source: destination.source },
      }));
    }

    async updateCart(eventCart) {
      this.request?.abort();
      clearTimeout(this.timer);
      const sequence = ++this.sequence;
      this.progress.hidden = true;
      this.price.textContent = 'Updating shipping estimate…';
      const complete = (cart) => Array.isArray(cart?.items) && Number.isSafeInteger(cart.total_price) && cart.currency;
      // Add responses can contain just the added lines. Read the real cart
      // before quoting instead of retaining an obsolete free-shipping offer.
      const cart = complete(eventCart) ? eventCart : await currentCart();
      if (sequence !== this.sequence || !this.isConnected) return;
      if (!complete(cart)) {
        this.price.textContent = 'Shipping calculated at checkout.';
        return;
      }
      this.data = { subtotalCents: cart.total_price, items: cart.items.map((item) => ({
        variantId: item.variant_id, quantity: item.quantity, requiresShipping: item.requires_shipping,
        giftCard: item.gift_card, bundle: !!item.item_components?.length,
      })) };
      this.dataset.currency = cart.currency;
      if (this.started) this.refresh();
    }

    scheduleRefresh() {
      this.request?.abort();
      this.sequence++;
      this.progress.hidden = true;
      this.price.textContent = 'Updating shipping estimate…';
      clearTimeout(this.timer);
      this.timer = setTimeout(() => { if (this.started) this.refresh(); }, 250);
    }

    payload() {
      const data = { ...this.data, items: this.data.items.map((item) => ({ ...item })) };
      if (this.dataset.context === 'product') {
        const scope = this.closest('product-info');
        const input = scope?.querySelector('input[name="quantity"]');
        const variant = scope?.querySelector('product-form input[name="id"]');
        if (variant && String(data.items[0]?.variantId) !== variant.value) return null;
        data.items[0].quantity = Math.max(1, Number(input?.value || 1));
      }
      return { ...data, context: this.dataset.context, currency: this.dataset.currency,
        destination: { country: this.destination.country, region: regions[this.destination.country] ? this.destination.region : '' },
      };
    }

    async refresh() {
      this.request?.abort();
      this.request = new AbortController();
      clearTimeout(this.requestTimer);
      const request = this.request;
      const signal = request.signal;
      this.requestTimer = setTimeout(() => request.abort(), 12000);
      const sequence = ++this.sequence;
      this.progress.hidden = true;
      const payload = this.payload();
      if (!payload) { this.price.textContent = 'Select an available option for a shipping estimate.'; return; }
      if (this.dataset.configured === 'true') {
        this.price.textContent = 'Shipping estimated in cart after configuration.';
        return;
      }
      if (!payload.items.length) { this.hidden = true; return; }
      this.hidden = false;
      if (this.dataset.context === 'cart' && payload.items.every((item) => item.requiresShipping === false && !item.bundle)) {
        this.price.textContent = 'No shipping required.';
        this.details.hidden = true;
        return;
      }
      this.price.textContent = 'Checking shipping…';
      this.querySelector('[data-shipping-note]').textContent = 'Final options and totals are confirmed at checkout.';
      try {
        let result;
        try {
          result = await readJSON(endpoint, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), signal,
          });
        } catch (error) {
          if (signal.aborted || !this.destination.postal || this.dataset.context !== 'cart') throw error;
          result = { status: 'unavailable' };
        }
        if (sequence !== this.sequence || !this.isConnected) return;
        this.render(result);
        if (this.dataset.context === 'product' && result.freeShipping) {
          this.cartProgress(sequence, signal);
        }
        if (this.dataset.context === 'cart' && this.destination.postal && result.status !== 'not_required') {
          try {
            const rates = await this.cartRates(signal);
            if (sequence !== this.sequence || !this.isConnected) return;
            const cheapest = rates?.filter((rate) => /^[A-Z]{3}$/.test(rate.currency || '') && Number.isFinite(Number(rate.price)) && Number(rate.price) >= 0)
              .filter((rate, _, all) => rate.currency === all[0].currency)
              .sort((a, b) => Number(a.price) - Number(b.price))[0];
            if (cheapest) {
              this.price.textContent = `Estimated shipping: ${Number(cheapest.price) === 0 ? 'Free' : this.money(Number(cheapest.price) * 100, cheapest.currency)} (${cheapest.name})`;
              // A live quote takes precedence over any threshold prediction.
              if (Number(cheapest.price) > 0 && result.amountCents === 0) this.progress.hidden = true;
              if (Number(cheapest.price) === 0) { this.progress.textContent = 'Your cart qualifies for free shipping.'; this.progress.hidden = false; }
            } else {
              this.price.textContent = 'No shipping options found for this destination. Check your postal code.';
              this.progress.hidden = true;
            }
          } catch (error) {
            if (sequence !== this.sequence || !this.isConnected) return;
            this.querySelector('[data-shipping-note]').textContent = 'Live rates could not be confirmed. Check your postal code and region or continue to checkout.';
            this.details.open = true;
            if (result.status !== 'estimated') this.price.textContent = 'Check your destination to estimate shipping.';
          }
        }
      } catch (_) {
        if (sequence !== this.sequence || !this.isConnected) return;
        this.price.textContent = 'Shipping calculated at checkout. You can still place your order.';
        this.progress.hidden = true;
      }
    }

    async cartProgress(sequence, signal) {
      // Show actual cart progress when shopping with an existing cart; never
      // count a hypothetical add before Shopify applies discounts/bundle rules.
      try {
        const cart = await currentCart();
        if (sequence !== this.sequence || signal.aborted || !this.isConnected || !cart?.items?.length) return;
        this.progress.hidden = true;
        const result = await readJSON(endpoint, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, signal,
          body: JSON.stringify({ context: 'cart', currency: cart.currency, subtotalCents: cart.total_price,
            destination: this.payload().destination,
            items: cart.items.map((item) => ({ variantId: item.variant_id, quantity: item.quantity })),
          }),
        });
        if (sequence !== this.sequence || signal.aborted || !this.isConnected || !result.freeShipping) return;
        if (result.freeShipping.remainingCents > 0) {
          this.progress.textContent = `Your cart is ${this.money(result.freeShipping.remainingCents, result.currency)} away from free standard shipping.`;
        } else if (result.amountCents === 0) {
          this.progress.textContent = 'Your current cart qualifies for free standard shipping.';
        } else return;
        this.progress.hidden = false;
      } catch (_) { /* The selection estimate remains useful on its own. */ }
    }

    money(cents, currency) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency, currencyDisplay: currency === this.dataset.currency ? 'symbol' : 'code',
      }).format(cents / 100);
    }

    render(result) {
      this.details.hidden = false;
      const currency = result.currency || 'USD';
      const scope = this.dataset.context === 'product' && this.dataset.presentation !== 'price' ? ' for this selection' : '';
      if (result.status === 'estimated' && Number.isFinite(result.amountCents)) {
        const price = result.amountCents === 0 ? 'Free' : this.money(result.amountCents, currency);
        this.price.textContent = result.estimateKind === 'from'
          ? `Estimated ${result.label || 'standard'} shipping${scope}: from ${price}`
          : `Estimated shipping${scope}: ${price}${result.label ? ` (${result.label})` : ''}`;
        const free = result.freeShipping;
        if (free && free.remainingCents > 0) {
          this.progress.textContent = `Add ${this.money(free.remainingCents, currency)} more in eligible items for free standard shipping.`;
          this.progress.hidden = false;
        }
      } else if (result.status === 'needs_region') {
        this.price.textContent = 'Choose your state for a shipping estimate.';
      } else if (result.status === 'not_required') {
        this.price.textContent = 'No shipping required.';
        this.details.hidden = true;
      } else {
        this.price.textContent = this.dataset.context === 'cart'
          ? 'Enter your postal code to estimate shipping.'
          : 'Shipping for this selection is estimated in cart.';
      }
    }

    async cartRates(signal) {
      const root = window.Shopify?.routes?.root || '/';
      const params = new URLSearchParams({
        'shipping_address[country]': this.destination.country,
        'shipping_address[province]': this.destination.region || '',
        'shipping_address[zip]': this.destination.postal,
      });
      await readJSON(`${root}cart/prepare_shipping_rates.json?${params}`, { method: 'POST', signal });
      for (let attempt = 0; attempt < 8; attempt++) {
        await new Promise((resolve, reject) => {
          const aborted = () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); };
          const timer = setTimeout(() => { signal.removeEventListener('abort', aborted); resolve(); }, 750);
          if (signal.aborted) aborted();
          else signal.addEventListener('abort', aborted, { once: true });
        });
        const result = await readJSON(`${root}cart/async_shipping_rates.json?${params}`, { signal });
        if (Array.isArray(result?.shipping_rates)) return result.shipping_rates;
      }
      throw new Error('Shipping rates timed out');
    }
  }
  customElements.define('shipping-estimate', ShippingEstimate);
})();
